import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  focusRing,
  PlatformDialog,
  Grid2 as Grid,
  Typography,
} from "@platform/style";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV } from "@fortawesome/sharp-solid-svg-icons";
import * as React from "react";
import { WorkGroup } from "../../models/WorkGroup";
import { fetchWithToken, hasPermission } from "@platform/core";
import { reportError } from "../../utils/errors";
import { WORK_MANAGEMENT_ADMIN_BFF_API_BASE } from "../../platform-workitems";

interface WorkGroupRowMenuProps {
  selectedGroup: WorkGroup;
  viewChange: (grp: WorkGroup) => void;
  snackBarDetails?: (message: string, open: boolean) => void;
  onError?: (message: string) => void;
  refetch: Function;
}

export const WorkGroupRowMenu = (props: WorkGroupRowMenuProps) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = React.useState<boolean>(false);
  const [allowAssignPeopleGroup, setAllowAssignPeopleGroup] = React.useState<boolean>(false);
  const [allowAssignTemplatesOrGroup, setAllowAssignTemplatesOrGroup] =
    React.useState<boolean>(false);
  const open = Boolean(anchorEl);
  React.useEffect(() => {
    (async () => {
      const assignPeopleGroupPermission = await hasPermission(
        "WorkManagementAdmin.assignPeopleGroup"
      );
      const assignTemplatesOrGroupPermission = await hasPermission(
        "WorkManagementAdmin.assignTemplatesOrGroup"
      );
      setAllowAssignPeopleGroup(assignPeopleGroupPermission);
      setAllowAssignTemplatesOrGroup(assignTemplatesOrGroupPermission);
    })();
  }, []);
  const handleMenuButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteSubmit = () => {
    setOpenDeleteDialog(true);
    handleMenuClose();
  };

  const handleDeleteClose = () => {
    setOpenDeleteDialog(false);
  };

  const deleteWorkGroup = async () => {
    await fetchWithToken(
      `${WORK_MANAGEMENT_ADMIN_BFF_API_BASE}/work-groups/${props.selectedGroup.WorkGroupIdentifier}`,
      { method: "DELETE" }
    )
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(
            JSON.stringify({
              statusCode: response.status,
              statusText: response.statusText,
              body: await response.json(),
            })
          );
        }
        props.refetch();
        if (props.snackBarDetails) {
          props.snackBarDetails(
            "Work Group " + props.selectedGroup.Name + " deleted successfully",
            true
          );
        }
      })
      .catch((error) => {
        if (props.onError) {
          if (!(error.message.indexOf("Cannot delete") === -1)) {
            props.onError(
              `Unable to delete work group ${props.selectedGroup.Name} because it is assigned to a Work Item Template.`
            );
          } else {
            props.onError(
              `Unable to delete work group ${props.selectedGroup.Name}. Please try again.`
            );
          }
        }
        reportError(error.message);
      });

    setOpenDeleteDialog(false);
  };

  return (
    <>
      <IconButton
        aria-label={`work group ${props.selectedGroup.WorkGroupIdentifier} menu`}
        id={`work-group-${props.selectedGroup.WorkGroupIdentifier}-button`}
        aria-controls={
          open ? `work group ${props.selectedGroup.WorkGroupIdentifier} menu` : undefined
        }
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleMenuButtonClick}
        size="small"
        sx={{ boxSizing: "content-box", padding: 2 }}
      >
        <Box sx={{ color: "primary.main" }}>
          <FontAwesomeIcon icon={faEllipsisV} data-testid={"WorkItemMoreIcon"} />
        </Box>
      </IconButton>
      <Menu
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        anchorEl={anchorEl}
        open={open}
        sx={{ width: "150px", borderRadius: "5px", marginTop: "-32px !important" }}
      >
        {allowAssignPeopleGroup && allowAssignTemplatesOrGroup && (
          <MenuItem onClick={() => props.viewChange(props.selectedGroup)} sx={focusRing}>
            Edit Group
          </MenuItem>
        )}
        <MenuItem onClick={handleDeleteSubmit} sx={focusRing}>
          Delete Group
        </MenuItem>
      </Menu>

      <PlatformDialog
        destructive
        open={openDeleteDialog}
        onSubmit={deleteWorkGroup}
        onClose={handleDeleteClose}
        submitLabel={"Yes, delete work group"}
        title={"Delete work group?"}
      >
        <Grid
          container
          direction="column"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={2}
          sx={{ mt: "10px" }}
        >
          <Box>
            <Typography variant={"h5"}>
              Are you sure you want to delete work group {props.selectedGroup.Name}?
            </Typography>
          </Box>
          <Box>
            <Typography variant={"body3"}>This action is final and cannot be undone.</Typography>
          </Box>
        </Grid>
      </PlatformDialog>
    </>
  );
};
